import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@quorum/environments';
import { Address, ServiceAppointment, Vehicle, WorkOrder } from '@quorum/models/xs-resource';
//import fontkit from '@pdf-lib/fontkit';
import { PDFDict, PDFDocument, PDFForm, PDFName } from 'pdf-lib';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private formUrl = '';
  triggerPDFGeneration$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) {}

  async generateHyuICEPdf(vehicleId: string, workOrderId: string, rooftopId: string): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/Hyundai VIR ICE English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrderId).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      // const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // SIGNATURE PLACEHOLDER START
      // Load the signature image
      // const signatureBytes = await fetch('assets/signature.png').then((res) => res.arrayBuffer());
      // const signatureImage = await pdfDoc.embedPng(signatureBytes);

      // const customerSignatureField = form.getSignature('customerSignature');
      // const widgets = customerSignatureField.acroField.getWidgets();

      // widgets.forEach((w) => {
      //   const rect = w.getRectangle();
      //   const page = pdfDoc.getPages()[0];

      //   // Draw the signature image over the field
      //   page.drawImage(signatureImage, {
      //     x: rect.x,
      //     y: rect.y,
      //     width: rect.width,
      //     height: rect.height,
      //   });
      // });
      // SIGNATURE PLACEHOLDER END

      // Populate the form fields
      this.populateSettings(form, data.inspection);
      this.populateVehicleInfo(form, data.inspection, data.vehicle);
      this.populateVisualChecks(form, data.inspection);
      this.populateTires(form, data.inspection);
      this.populateTireOther(form, data.inspection);
      this.populateBrakes(form, data.inspection, data.brakeMeasurements);
      this.populateCheckAndFill(form, data.inspection, 'ICE');
      this.populateBattery(form, data.inspection);
      this.populateSafety(form, data.inspection);
      this.populateService1ICE(form, data.inspection);
      this.populateService2ICE(form, data.inspection);
      this.populateService3ICE(form, data.inspection);
      this.populateAdditionalRecommendations(form, data.inspection, 'ICE');
      // this.populateNextVisit(form, data.inspection); //
      // this.populateCompleted(form, data.appointment);

      form.flatten();

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async generateHyuEVPdf(vehicleId: string, workOrderId: string, rooftopId: string): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/Hyundai VIR EV English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrderId).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF and font files
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      // const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // SIGNATURE PLACEHOLDER START
      // Load the signature image
      // const signatureBytes = await fetch('assets/signature.png').then((res) => res.arrayBuffer());
      // const signatureImage = await pdfDoc.embedPng(signatureBytes);

      // const customerSignatureField = form.getSignature('customerSignature');
      // const widgets = customerSignatureField.acroField.getWidgets();

      // widgets.forEach((w) => {
      //   const rect = w.getRectangle();
      //   const page = pdfDoc.getPages()[0];

      //   // Draw the signature image over the field
      //   page.drawImage(signatureImage, {
      //     x: rect.x,
      //     y: rect.y,
      //     width: rect.width,
      //     height: rect.height,
      //   });
      // });
      // SIGNATURE PLACEHOLDER END

      // Populate the form fields
      this.populateSettings(form, data.inspection);
      this.populateVehicleInfo(form, data.inspection, data.vehicle);
      this.populateVisualChecks(form, data.inspection);
      this.populateTires(form, data.inspection);
      this.populateTireOther(form, data.inspection);
      this.populateBrakes(form, data.inspection, data.brakeMeasurements);
      this.populateCheckAndFill(form, data.inspection, 'EV');
      this.populateBattery(form, data.inspection);
      this.populateSafety(form, data.inspection);
      this.populateService1EV(form, data.inspection);
      this.populateService2EV(form, data.inspection);
      this.populateService3EV(form, data.inspection);
      this.populateAdditionalRecommendations(form, data.inspection, 'EV');
      // this.populateNextVisit(form, data.inspection);
      // this.populateCompleted(form, data.appointment);

      form.flatten();

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async generateGMCanadaICEPdf(
    vehicleId: string,
    rooftopId: string,
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder
  ): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/GM ICE MPVI Canada English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrder.id.toString()).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // Populate the form fields
      this.populateGMCustomerInfo(form, data.vehicle, customerAddress, appointment, workOrder);
      this.populateGMVehicleInfo(form, data.vehicle, appointment, workOrder);
      this.populateGMVisitInfo(form, data.inspection, appointment, workOrder);
      this.populateGMMiscChecks(form, data.inspection, 'ICE');
      this.populateGMOil(form, data.inspection);
      this.populateGM12VBattery(form, data.inspection);
      this.populateGMLights(form, data.inspection);
      //this.populateGMDashboardLights(form, data.inspection); // TODO: no dashboard light on inspections
      this.populateGMWipers(form, data.inspection);
      this.populateGMTires(form, data.inspection);
      this.populateGMTireOther(form, data.inspection);
      this.populateGMBrakes(form, data.inspection);
      this.populateGMFluids(form, data.inspection, 'ICE');
      this.populateGMCheckInspect(form, data.inspection, 'ICE');

      this.fixBroken123GMICERadioGroups(form);
      this.handle321ChoiceTasks(form, data.inspection);

      form.flatten();

      // Serialize the PDFDocument to bytes

      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async generateGMCanadaEVPdf(
    vehicleId: string,
    rooftopId: string,
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder
  ): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/GM EV MPVI Canada English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrder.id.toString()).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      // const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // Populate the form fields
      this.populateGMCustomerInfo(form, data.vehicle, customerAddress, appointment, workOrder);
      this.populateGMVehicleInfo(form, data.vehicle, appointment, workOrder);
      this.populateGMVisitInfo(form, data.inspection, appointment, workOrder);
      this.populateGMMiscChecks(form, data.inspection, 'EV');
      this.populateGMOil(form, data.inspection);
      this.populateGM12VBattery(form, data.inspection);
      this.populateGMLights(form, data.inspection);
      //this.populateGMDashboardLights(form, data.inspection); // TODO: no dashboard light on inspections
      this.populateGMWipers(form, data.inspection);
      this.populateGMTires(form, data.inspection);
      this.populateGMTireOther(form, data.inspection);
      this.populateGMBrakes(form, data.inspection);
      this.populateGMFluids(form, data.inspection, 'EV');
      this.populateGMCheckInspect(form, data.inspection, 'EV');

      this.fixBroken123GMEVRadioGroups(form);
      this.handle321ChoiceTasks(form, data.inspection);

      form.flatten();

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async generateGMUSAICEPdf(
    vehicleId: string,
    rooftopId: string,
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder
  ): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/GM ICE MPVI US English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrder.id.toString()).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      // const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // Populate the form fields
      this.populateGMCustomerInfo(form, data.vehicle, customerAddress, appointment, workOrder);
      this.populateGMVehicleInfo(form, data.vehicle, appointment, workOrder);
      this.populateGMVisitInfo(form, data.inspection, appointment, workOrder);
      this.populateGMMiscChecks(form, data.inspection, 'ICE');
      this.populateGMOil(form, data.inspection);
      this.populateGM12VBattery(form, data.inspection);
      this.populateGMLights(form, data.inspection);
      //this.populateGMDashboardLights(form, data.inspection); // TODO: no dashboard light on inspections
      this.populateGMWipers(form, data.inspection);
      this.populateGMTires(form, data.inspection);
      this.populateGMTireOther(form, data.inspection);
      this.populateGMBrakes(form, data.inspection);
      this.populateGMFluids(form, data.inspection, 'ICE');
      this.populateGMCheckInspect(form, data.inspection, 'ICE');

      this.fixBroken123GMICERadioGroups(form);
      this.handle321ChoiceTasks(form, data.inspection);

      form.flatten();

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  async generateGMUSAEVPdf(
    vehicleId: string,
    rooftopId: string,
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder
  ): Promise<Blob> {
    try {
      this.formUrl = `assets/oem-inspection-pdfs/GM EV MPVI US English.pdf`;

      // Wait for the required data
      const data = await combineLatest([
        this.getInspection(workOrder.id.toString()).pipe(filter((inspection) => inspection != null)),
        this.getVehicle(vehicleId).pipe(filter((vehicle) => vehicle != null)),
        this.getBrakeMeasurements(rooftopId).pipe(filter((brakeMeasurements) => brakeMeasurements != null)),
      ])
        .pipe(
          map(([inspection, vehicle, brakeMeasurements]) => ({
            inspection,
            vehicle,
            brakeMeasurements,
          }))
        )
        .toPromise();

      // Fetch the PDF
      const [formBytes] = await Promise.all([fetch(this.formUrl).then((res) => res.arrayBuffer())]);

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(formBytes);

      // Get the form
      const form = pdfDoc.getForm();

      // // Debugging field logs
      // const fields = form.getFields();

      // fields.forEach((field) => {
      //   const type = field.constructor.name;
      //   const name = field.getName();

      //   console.log(`Field Name: ${name} as ${type}`);
      // });

      // Populate the form fields
      this.populateGMCustomerInfo(form, data.vehicle, customerAddress, appointment, workOrder);
      this.populateGMVehicleInfo(form, data.vehicle, appointment, workOrder);
      this.populateGMVisitInfo(form, data.inspection, appointment, workOrder);
      this.populateGMMiscChecks(form, data.inspection, 'EV');
      this.populateGMOil(form, data.inspection);
      this.populateGM12VBattery(form, data.inspection);
      this.populateGMLights(form, data.inspection);
      //this.populateGMDashboardLights(form, data.inspection); // TODO: no dashboard light on inspections
      this.populateGMWipers(form, data.inspection);
      this.populateGMTires(form, data.inspection);
      this.populateGMTireOther(form, data.inspection);
      this.populateGMBrakes(form, data.inspection);
      this.populateGMFluids(form, data.inspection, 'EV');
      this.populateGMCheckInspect(form, data.inspection, 'EV');

      this.fixBroken123GMEVRadioGroups(form);
      this.handle321ChoiceTasks(form, data.inspection);

      form.flatten();

      // Serialize the PDFDocument to bytes
      const pdfBytes = await pdfDoc.save();

      // Convert to a Blob and return
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  getInspection(inspectionId: string) {
    return this.http.get(`${environment.apiBase}/v/2/vip/inspections/${inspectionId}`).pipe(take(1));
  }

  getVehicle(vehicleId: string): Observable<Vehicle> {
    return this.http
      .get<Vehicle>(`${environment.apiBase}/v/2/vehicles/vehicles/${vehicleId}`, {
        params: {
          embed: 'associate',
        },
      })
      .pipe(take(1));
  }

  getBrakeMeasurements(rooftopId: string) {
    return this.http
      .get(`${environment.apiBase}/v/2/vip/vehicle-brake-measurements`, {
        params: { rooftopId },
      })
      .pipe(take(1));
  }

  getAppointmentDetails(appointmentId: string) {
    return this.http
      .get<any[]>(`${environment.apiBase}/v/2/fixed-operations/appointments`, {
        params: {
          id: appointmentId,
          embed: 'serviceAdvisorAssociate',
        },
      })
      .pipe(
        take(1),
        map((appointments: any[]) => appointments[0])
      );
  }

  determinePowerLaneURL(): string {
    switch (environment.environment) {
      case 'development':
        return 'http://localhost:4200';
      case 'staging':
        return 'https://staging-service-lane.xsellerator.com';
      case 'production':
        return 'https://power-lane.xsellerator.com';
      default:
        return 'https://staging-service-lane.xsellerator.com';
    }
  }

  populateSettings(form: PDFForm, inspection: any) {
    const miscellaneousCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Miscellaneous'
    );
    if (!miscellaneousCategory) return;

    const tasks = [
      { id: 'HYU_CUSTOMER_SETTINGS_RETURNED', field: 'settings' },
      { id: 'HYU_RECALLS_CHECKED', field: 'recalls' },
      { id: 'HYU_RESET_SERVICE_REMINDERS', field: 'reminders' },
      { id: 'HYU_CUSTOMER_DECLINED_MAINTENANCE', field: 'declinedMaintenance' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = miscellaneousCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) {
        this.checkGTask(form, task, field);
      }
    });
  }

  populateVehicleInfo(form: PDFForm, inspection: any, vehicle: any) {
    this.setFormField(form, 'VIN', vehicle.vin);
    //this.setFormField(form, 'dealership', 'Dealer Info'); // PLACEHOLDER
    this.setFormField(
      form,
      'customerName',
      `${vehicle.embedded?.associate?.firstName} ${vehicle.embedded?.associate?.lastName}`
    );
    this.setFormField(form, 'repairOrderNumber', inspection.workOrderId?.toString());
    this.setFormField(form, 'odometer', vehicle.currentMileage?.toString());
    this.setFormField(form, 'vehicleModel', vehicle.model);
    this.setFormField(form, 'vehicleYear', vehicle.year?.toString());

    //this.setFormField(form, 'serviceNumber', '80085'); // PLACEHOLDER
    this.setFormField(form, 'licensePlateNumber', vehicle.licensePlateNumber);

    const visitDate = inspection.inspectionDate.split('T')[0].split('-');

    this.setFormField(form, 'visitMonth', visitDate[1]);
    this.setFormField(form, 'visitDay', visitDate[2]);
    this.setFormField(form, 'visitYear', visitDate[0]);
  }

  populateVisualChecks(form: PDFForm, inspection: any) {
    const visualCheckCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Visually Check Following Components'
    );
    if (!visualCheckCategory) return;

    const checkItems = [
      { taskName: 'Windshield', prefix: 'visualCheckWindshield' },
      { taskName: 'Wiper Blades', prefix: 'visualCheckWipers' },
      { inspectionId: 'HYU_HORN', prefix: 'visualCheckHorn' },
      { taskName: 'Interior Lights', prefix: 'visualCheckInteriorLights' },
      { taskName: 'Exterior Lights', prefix: 'visualCheckExteriorLights' },
    ];

    checkItems.forEach(({ taskName, inspectionId, prefix }) => {
      const task = taskName
        ? visualCheckCategory.tasks.find((task: any) => task.taskName.includes(taskName))
        : visualCheckCategory.tasks.find((task: any) => task.inspectionId.includes(inspectionId));

      if (task) {
        this.checkGRTaskWithPrefix(form, task, prefix);
      }
    });

    // Body condition comments (Placeholder: Uncomment and modify if needed)
    // this.setFormField(form, 'bodyConditionComments', 'Custom body condition comments here.');
  }

  populateTires(form: PDFForm, inspection: any) {
    const tiresCategory = inspection.tires?.find((tires: any) => tires.categoryName === 'Check Tires');
    if (!tiresCategory) return;

    const tirePositions = [
      { id: 'Tires - LF', prefix: 'tiresLF' },
      { id: 'Tires - LR', prefix: 'tiresLR' },
      { id: 'Tires - RF', prefix: 'tiresRF' },
      { id: 'Tires - RR', prefix: 'tiresRR' },
    ];

    let frontPSI = '';
    let backPSI = '';

    tirePositions.forEach(({ id, prefix }) => {
      const task = tiresCategory.tasks.find((task: any) => task.taskName.includes(id));
      if (!task) return;

      this.checkGYRTaskWithPrefix(form, task, prefix);

      this.setFormField(form, `${prefix}_tread`, task.treadDepth?.toString());
      this.setFormField(form, `${prefix}_wear`, task.wearPattern);

      if (id === 'Tires - LF') frontPSI = task.psiOut?.toString() || '';
      if (id === 'Tires - LR') backPSI = task.psiOut?.toString() || '';
    });

    // PLACEHOLDER fields for front and back PSI
    this.setFormField(form, 'tires_frontPSI', frontPSI);
    this.setFormField(form, 'tires_backPSI', backPSI);
  }

  populateTireOther(form: PDFForm, inspection: any) {
    const tiresOtherCategory = inspection.tireOther?.find((tires: any) => tires.categoryName === 'Check Tires');

    if (!tiresOtherCategory) return;

    const tasksToCheck = [
      { id: 'Rotation', key: 'tires_rotation' },
      { id: 'Alignment', key: 'tires_alignment' },
      { id: 'Replacement', key: 'tires_replacement' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = tiresOtherCategory.tasks.find((task: any) => task.taskName.includes(id));
      if (task) this.checkRTask(form, task, key);
    });
  }

  populateBrakes(form: PDFForm, inspection: any, brakeMeasurements: any) {
    const brakeCheckCategory = inspection.categories?.find((category: any) => category.categoryName === 'Check Brakes');
    if (!brakeCheckCategory) return;

    const tasks = [
      { name: 'Brakes - LF', field: 'brakesLF' },
      { name: 'Brakes - RF', field: 'brakesRF' },
      { name: 'Brakes - LR', field: 'brakesLR' },
      { name: 'Brakes - RR', field: 'brakesRR' },
    ];

    const brakeInspectionTask = brakeCheckCategory.tasks.find((task: any) =>
      task.inspectionId.includes('HYU_BRAKE_INSPECTION')
    );
    const brakeServiceTask = brakeCheckCategory.tasks.find((task: any) =>
      task.inspectionId.includes('HYU_BRAKE_SERVICE_TOGGLE')
    );

    if (brakeInspectionTask) this.checkGTask(form, brakeInspectionTask, 'brakes_inspection');
    if (brakeServiceTask) this.checkGTask(form, brakeServiceTask, 'brakes_service');

    const brakeMeasurementIds = ['G', 'Y', 'R'].map(
      (code) => brakeMeasurements.find((brake: any) => brake.gmBrakeCode === code)?.id
    );

    tasks.forEach(({ name, field }) => {
      const task = brakeCheckCategory.tasks.find((task: any) => task.taskName.includes(name));
      if (task) {
        this.checkBrakeTaskWithPrefix(form, task, field, brakeMeasurementIds);
      }
    });
  }

  populateCheckAndFill(form: PDFForm, inspection: any, inspectionType: 'ICE' | 'EV') {
    const checkAndFillCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check & Fill'
    );

    if (!checkAndFillCategory) return;

    const tasksToCheck = [
      { name: 'Window Washer', key: 'checkWasher' },
      { name: 'Brake', key: 'checkBrake' },
    ];

    if (inspectionType === 'ICE') {
      tasksToCheck.push(
        { name: 'Engine Oil', key: 'checkOil' },
        { name: 'Coolant', key: 'checkCoolant' },
        { name: 'Transmission (if equipped with dipstick)', key: 'checkTransmission' },
        { name: 'Clutch (if equipped)', key: 'checkClutch' }
      );
    }

    tasksToCheck.forEach(({ name, key }) => {
      const task = checkAndFillCategory.tasks.find((task: any) => task.taskName.includes(name));
      if (task) this.checkOkFilledTask(form, task, key);
    });
  }

  populateBattery(form: PDFForm, inspection: any) {
    const batteryCategory = inspection.categories.find((category: any) => category.categoryName === 'Check Battery');

    if (!batteryCategory) return;

    const tasksToCheck = [
      { id: 'Battery Voltage', key: 'batteryVoltage', checkFn: this.checkGRTaskWithPrefix },
      { id: 'Battery Cables & Connections', key: 'batteryCables', checkFn: this.checkGYRTaskWithPrefix },
    ];

    let batteryVoltageTask: any = null;

    tasksToCheck.forEach(({ id, key, checkFn }) => {
      const task = batteryCategory.tasks.find((task: any) => task.taskName.includes(id));
      if (task) {
        checkFn.call(this, form, task, key);
        if (id === 'Battery Voltage') batteryVoltageTask = task;
      }
    });

    if (batteryVoltageTask) {
      this.setFormField(form, 'batteryCCA_factory', batteryVoltageTask.factorySpecs?.toString());
      this.setFormField(form, 'batteryCCA_actual', batteryVoltageTask.actual?.toString());
    }
  }

  populateSafety(form: PDFForm, inspection: any) {
    const safetyCategory = inspection.categories.find((category: any) => category.categoryName === 'Safety');

    if (!safetyCategory) return;

    const tasksToCheck = [
      { id: 'HYU_FLOOR_LINERS_CHECK', key: 'safetyLiners' },
      { id: 'HYU_CORRECT_TIRES_CHECK', key: 'safetyTires' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = safetyCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkYNTaskWithPrefix(form, task, key);
    });
  }

  populateService1ICE(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_ENGINE_OIL_FILTER', key: 'service1Oil' },
      { id: 'HYU_DRIVESHAFTS_AND_BOOTS', key: 'service1Driveshaft' },
      { id: 'HYU_GEAR_BOX_LINKAGES_BOOTS_JOINTS', key: 'service1GearBox' },
      { id: 'HYU_VACUUM_HOSE', key: 'service1Vacuum' },
      { id: 'HYU_LATCHES_HINGES_LOCKS', key: 'service1Latches' },
      { id: 'HYU_BRAKE_SYSTEM_LINES_HOSES_PARKING', key: 'service1Brake' },
      { id: 'HYU_EXHAUST_SYSTEM_MUFFLER', key: 'service1Exhaust' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateService2ICE(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_COOLING_SYSTEM_HOSES_CONNECTIONS', key: 'service2Cooling' },
      { id: 'HYU_SUSPENSION_MOUNTING_BOLTS', key: 'service2Suspension' },
      { id: 'HYU_PARKING_CLUTCH_BRAKE_PEDAL_FREE_PLAY', key: 'service2PBrake' },
      { id: 'HYU_AIR_INTAKE_HOSE', key: 'service2AirIntake' },
      { id: 'HYU_AC_HEATING_FUNCTION', key: 'service2ACHeating' },
      { id: 'HYU_AC_COMPRESSOR_FUNCTION', key: 'service2ACCompressor' },
      { id: 'HYU_CABIN_AIR_FILTER_REPLACE', key: 'service2CabinFilter' },
      { id: 'HYU_ENGINE_AIR_FILTER_INSPECT', key: 'service2EngineFilter' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateService3ICE(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_FUEL_FILLER_CAP', key: 'service3FuelCap' },
      { id: 'HYU_FUEL_TANK', key: 'service3FuelTank' },
      { id: 'HYU_FUEL_LINES_HOSES_CONNECTIONS', key: 'service3FuelLines' },
      { id: 'HYU_VAPOR_HOSE', key: 'service3VaporHose' },
      { id: 'HYU_CANNISTER', key: 'service3Cannister' },
      { id: 'HYU_ENGINE_AIR_FILTER_REPLACE', key: 'service3EngineFilter' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateService1EV(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_BATTERY_STATE_OF_CHARGE', key: 'service1SOC' },
      { id: 'HYU_BATTERY_STATE_OF_HEALTH', key: 'service1SOH' },
      { id: 'HYU_LATCHES_HINGES_LOCKS', key: 'service1Latches' },
      { id: 'HYU_DRIVESHAFTS_AND_BOOTS', key: 'service1Driveshaft' },
      { id: 'HYU_STEERING_LINKAGES', key: 'service1Steering' },
      { id: 'HYU_CHECK_COOLANT_LEVEL', key: 'service1Coolant' },
      { id: 'HYU_AIR_CONDITION_REFRIGERANT', key: 'service1Refrigerant' },
      { id: 'HYU_ELECTRICAL_SYSTEMS', key: 'service1Electrical' },
      { id: 'HYU_BOLTS_NUTS_CHASSIS_BODY', key: 'service1BoltsNuts' },
      { id: 'HYU_FRONT_SUSPENSION_BALL_JOINTS', key: 'service1FrontSuspension' },
      { id: 'HYU_BRAKE_PEDAL_PLAY', key: 'service1BrakePedal' },
      { id: 'HYU_AIR_CONDITION_COMPRESSOR', key: 'service1Compressor' },
      { id: 'HYU_BRAKE_FLUID_CHECK', key: 'service1BrakeFluid' },
      { id: 'HYU_COOLING_SYSTEM_HOSES_CONNECTIONS', key: 'service1CoolingSystem' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateService2EV(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_AC_HEATING_FUNCTIONS', key: 'service2ACHeating' },
      { id: 'HYU_CABIN_AIR_FILTER', key: 'service2CabinAirFilter' },
      { id: 'HYU_BRAKE_SYSTEM_LINES_HOSES_PARKING', key: 'service2BrakeSystem' },
      { id: 'HYU_SHOCKS_STRUTS_SUSPENSION', key: 'service2Shocks' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateService3EV(form: PDFForm, inspection: any) {
    const serviceCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Check Following Systems & Components'
    );

    if (!serviceCategory) return;

    const tasksToCheck = [
      { id: 'HYU_VISUAL_FLUID_LEAKS', key: 'service3Leaks' },
      { id: 'HYU_BRAKE_SERVICE', key: 'service3Brakes' },
    ];

    tasksToCheck.forEach(({ id, key }) => {
      const task = serviceCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkGYRTaskWithPrefix(form, task, key);
    });
  }

  populateAdditionalRecommendations(form: PDFForm, inspection: any, inspectionType: 'ICE' | 'EV') {
    const miscellaneousCategory = inspection.categories.find(
      (category: any) => category.categoryName === 'Miscellaneous'
    );

    if (!miscellaneousCategory) return;

    const baseTasks = [
      { id: 'HYU_FLOOR_LINERS_INSTALLED', key: 'recommendationsFloorLiners' },
      { id: 'HYU_SEASONAL_TIRES_CHANGED', key: 'recommendationsSeasonalTireChange' },
      { id: 'HYU_SEALANT_EXPIRATION_CHECK', key: 'recommendationsSealant' },
    ];

    const iceTasks = [{ id: 'HYU_FUEL_INJECTOR_CLEANER', key: 'recommendationsFuelCleaner' }];

    const evTasks = [
      { id: 'HYU_V2L_INSTALLED', key: 'recommendationsV2L' },
      { id: 'HYU_LOW_CONDUCTIVITY_COOLANT_REPLACEMENT', key: 'recommendationsCoolant' },
    ];

    // Merge base tasks with ICE or EV-specific tasks
    const tasksToCheck = baseTasks.concat(inspectionType === 'ICE' ? iceTasks : evTasks);

    tasksToCheck.forEach(({ id, key }) => {
      const task = miscellaneousCategory.tasks.find((task: any) => task.inspectionId.includes(id));
      if (task) this.checkYNTaskWithPrefix(form, task, key);
    });

    //this.setFormField(form, 'recommendationsComments', 'Some default recommendation text.'); // PLACEHOLDER
  }

  // all mocked for now
  populateNextVisit(form: PDFForm, inspection: any) {
    //this.setFormField(form, 'nextServiceDate', 'March 1st, 2025'); // PLACEHOLDER
    //this.setFormField(form, 'nextServiceTime', '11:00 am'); // PLACEHOLDER
    //this.setFormField(form, 'nextServiceMileage', '35000'); // PLACEHOLDER

    const recommendedService1Checkbox = form.getCheckBox('recommendedService1');
    //recommendedService1Checkbox.check(); //PLACEHOLDER

    const recommendedService2Checkbox = form.getCheckBox('recommendedService2');
    //recommendedService2Checkbox.check(); //PLACEHOLDER

    const recommendedService3Checkbox = form.getCheckBox('recommendedService3');
    //recommendedService3Checkbox.check(); //PLACEHOLDER

    //this.setFormField(form, 'nextServicePrice', '250'); // PLACEHOLDER
  }

  private buildAdvisorName(appointment: ServiceAppointment | undefined): string {
    const serviceAdvisorAssociate = appointment?.embedded?.serviceAdvisorAssociate;
    return `${serviceAdvisorAssociate?.firstName ?? ''} ${serviceAdvisorAssociate?.lastName ?? ''}`;
  }

  populateCompleted(form: PDFForm, appointment: any) {
    this.setFormField(form, 'advisorName', this.buildAdvisorName(appointment));

    if (appointment?.technicianId) {
      this.setFormField(form, 'technicianName', appointment?.technicianGroupName);
    }
  }

  checkGYRTaskWithPrefix(form: PDFForm, task: any, prefix: string) {
    const greenCheckbox = form.getCheckBox(`${prefix}_G`);
    const yellowCheckbox = form.getCheckBox(`${prefix}_Y`);
    const redCheckbox = form.getCheckBox(`${prefix}_R`);

    if (task.greenBox) {
      greenCheckbox.check();
    } else if (task.yellowBox) {
      yellowCheckbox.check();
    } else if (task.redBox) {
      redCheckbox.check();
    }
  }

  checkGRTaskWithPrefix(form: PDFForm, task: any, prefix: string) {
    const greenCheckbox = form.getCheckBox(`${prefix}_G`);
    const redCheckbox = form.getCheckBox(`${prefix}_R`);

    if (task.greenBox) {
      greenCheckbox.check();
    } else if (task.redBox) {
      redCheckbox.check();
    }
  }

  checkGTask(form: PDFForm, task: any, pdfField: string) {
    const greenCheckbox = form.getCheckBox(pdfField);

    if (task.greenBox) {
      greenCheckbox.check();
    }
  }

  checkRTask(form: PDFForm, task: any, pdfField: string) {
    const redCheckbox = form.getCheckBox(pdfField);

    if (task.redBox) {
      redCheckbox.check();
    }
  }

  checkYNTaskWithPrefix(form: PDFForm, task: any, prefix: string) {
    const greenCheckbox = form.getCheckBox(`${prefix}_Y`);
    const redCheckbox = form.getCheckBox(`${prefix}_N`);

    if (task.greenBox) {
      greenCheckbox.check();
    } else if (task.redBox) {
      redCheckbox.check();
    }
  }

  checkBrakeTaskWithPrefix(form: PDFForm, task: any, prefix: string, brakeMeasurementIds: number[]) {
    const greenCheckbox = form.getCheckBox(`${prefix}_G`);
    const yellowCheckbox = form.getCheckBox(`${prefix}_Y`);
    const redCheckbox = form.getCheckBox(`${prefix}_R`);

    switch (task.brakeMeasurementId) {
      case brakeMeasurementIds[0]:
        greenCheckbox.check();
        break;
      case brakeMeasurementIds[1]:
        yellowCheckbox.check();
        break;
      case brakeMeasurementIds[2]:
        redCheckbox.check();
        break;
      default:
        break;
    }
  }

  checkOkFilledTask(form: PDFForm, task: any, prefix: string) {
    const okCheckbox = form.getCheckBox(`${prefix}_ok`);
    const filledCheckbox = form.getCheckBox(`${prefix}_filled`);

    if (task.greenBox) {
      okCheckbox.check();
    }

    if (task.fluidFilled) {
      filledCheckbox.check();
    }
  }

  // GM PDF FUNCTIONS START
  populateGMCustomerInfo(
    form: PDFForm,
    vehicle: Vehicle,
    customerAddress: Address,
    appointment: ServiceAppointment,
    workOrder: WorkOrder
  ) {
    this.setFormField(form, 'Prénom', vehicle.embedded?.associate?.firstName);
    this.setFormField(form, 'Nom', vehicle.embedded?.associate?.lastName);

    const phone = customerAddress?.cellPhoneNumber
      ? customerAddress.cellPhoneNumber
      : workOrder?.contactPhoneNumber
      ? workOrder.contactPhoneNumber
      : appointment?.phoneNumber
      ? appointment.phoneNumber
      : '';

    this.setFormField(form, 'Téléphone', phone);
    this.setFormField(form, 'Courriel', vehicle.embedded?.associate?.emailAddress);
  }

  populateGMVehicleInfo(form: PDFForm, vehicle: Vehicle, appointment: ServiceAppointment, workOrder: WorkOrder) {
    this.setFormField(form, 'Année', vehicle.year?.toString());
    this.setFormField(form, 'Fabricant', vehicle.make);
    this.setFormField(form, 'Modèle', vehicle.model);
    this.setFormField(form, 'NIV', vehicle.vin);
    this.setFormField(form, 'KM', vehicle.currentMileage?.toString());

    const tagNumber = workOrder?.tagNumber
      ? workOrder.tagNumber
      : appointment?.tagNumber
      ? appointment.tagNumber
      : 'N/A';

    this.setFormField(form, `No d'étiquette`, tagNumber);
  }

  populateGMVisitInfo(form: PDFForm, inspection: any, appointment: ServiceAppointment, workOrder: WorkOrder) {
    const serviceAdvisorName = this.buildAdvisorName(appointment);

    if (workOrder?.serviceAdvisorName) {
      this.setFormField(form, `Superviseur`, workOrder.serviceAdvisorName);
    } else {
      this.setFormField(form, `Superviseur`, serviceAdvisorName);
    }

    this.setFormField(form, 'No. de BR', workOrder.id.toString());

    if (appointment?.technicianId) {
      this.setFormField(form, 'Technicien', appointment?.technicianGroupName);
    }

    //this.setFormField(form, 'Prochain entretien', 'Next Service placeholder'); // PLACEHOLDER

    const visitDate = inspection.inspectionDate.split('T')[0].split('-');

    this.setFormField(form, 'Date du jour', `${visitDate[0]}-${visitDate[1]}-${visitDate[2]}`);
  }

  populateGMMiscChecks(form: PDFForm, inspection: any, inspectionType: 'ICE' | 'EV') {
    // recall, clean sensors + camera, car wash TODO: these need to be added to inspections???

    const recallCheckField = form.getCheckBox('Recall');
    //recallCheckField.check();

    let cleanSensorsField;
    if (inspectionType === 'ICE') {
      cleanSensorsField = form.getCheckBox('Clean');
    } else if (inspectionType === 'EV') {
      cleanSensorsField = form.getCheckBox('Clean sensors');
    }
    //cleanSensorsField.check();

    if (inspectionType === 'ICE') {
      const carWashField = form.getCheckBox('Wash');
      //carWashField.check();
    }

    // const miscellaneousCategory = inspection.categories?.find(
    //   (category: any) => category.categoryName === 'Miscellaneous'
    // );
    // if (!miscellaneousCategory) return;
    // const tasks = [
    //   { id: 'HYU_CUSTOMER_SETTINGS_RETURNED', field: 'Recall' },
    //   { id: 'HYU_RECALLS_CHECKED', field: 'Clean' },
    //   { id: 'HYU_RESET_SERVICE_REMINDERS', field: 'Wash' },
    // ];
    // tasks.forEach(({ id, field }) => {
    //   const task = miscellaneousCategory.tasks.find((task: any) => task.inspectionId.includes(id));
    //   if (task) {
    //     this.checkGTask(form, task, field);
    //   }
    // });
  }

  populateGMOil(form: PDFForm, inspection: any) {
    const oilCategory = inspection.categories?.find((category: any) => category.categoryName === 'Oil Life');
    if (!oilCategory) return;

    const oilLifePercent = oilCategory.tasks.find((task: any) => task.gmInspectionId.includes('OIL_LIFE')).oilLife;

    this.setFormField(form, '% Oil Life', oilLifePercent?.toString());

    const tasks = [{ id: 'OIL_LIFE', field: 'Oil life' }];

    //TODO Current inspection only has 2 oil tasks, need to add/find the others?
    tasks.forEach(({ id, field }) => {
      const task = oilCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const oilLifeField = form.getRadioGroup(field);
        const options = oilLifeField.getOptions();
        if (task.redBox) {
          oilLifeField.select(options[2]);
        } else if (task.yellowBox) {
          oilLifeField.select(options[1]);
        } else if (task.greenBox) {
          oilLifeField.select(options[0]);
        }
      }
    });
  }

  populateGM12VBattery(form: PDFForm, inspection: any) {
    const batteryCategory = inspection.categories?.find((category: any) => category.categoryName === 'Check Battery');
    if (!batteryCategory) return;

    const tasks = [
      { id: 'BATTERY_CONDITION', field: 'Battery test' },
      { id: 'BATTERY_VISUAL_INSPECTION', field: 'Battery visual' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = batteryCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const batteryField = form.getRadioGroup(field);
        const options = batteryField.getOptions();

        if (task.redBox) {
          batteryField.select(options[2]);
        } else if (task.yellowBox) {
          batteryField.select(options[1]);
        } else if (task.greenBox) {
          batteryField.select(options[0]);
        }
      }
    });
  }

  populateGMLights(form: PDFForm, inspection: any) {
    const lightsCategory = inspection.categories?.find((category: any) => category.categoryName === 'Check Lights');
    if (!lightsCategory) return;

    const tasks = [
      { id: 'EXTERIOR_LIGHTS', field: 'Headlamps' }, //TODO Only 1 light task on inspection, use for all 3
      { id: 'EXTERIOR_LIGHTS', field: 'Tail lamps' },
      { id: 'EXTERIOR_LIGHTS', field: 'Turn signals' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = lightsCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const lightsField = form.getRadioGroup(field);
        const options = lightsField.getOptions();

        if (task.redBox) {
          lightsField.select(options[2]);
        } else if (task.yellowBox) {
          lightsField.select(options[1]);
        } else if (task.greenBox) {
          lightsField.select(options[0]);
        }
      }
    });
  }

  populateGMDashboardLights(form: PDFForm, inspection: any) {
    const lightsCategory = inspection.categories?.find((category: any) => category.categoryName === 'Check Lights');
    if (!lightsCategory) return;

    const tasks = [
      { id: 'EXTERIOR_LIGHTS', field: 'Active dash lights' }, //TODO no dashboard lights task on inspection yet
    ];

    tasks.forEach(({ id, field }) => {
      const task = lightsCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const lightsField = form.getRadioGroup(field);
        const options = lightsField.getOptions();

        if (task.redBox) {
          lightsField.select(options[2]);
        } else if (task.yellowBox) {
          lightsField.select(options[1]);
        } else if (task.greenBox) {
          lightsField.select(options[0]);
        }
      }
    });
  }

  populateGMWipers(form: PDFForm, inspection: any) {
    const wipersCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Windshield & Wipers'
    );
    if (!wipersCategory) return;

    const tasks = [
      { id: 'WIPERS_LF', field: 'Windshield Driver front' },
      { id: 'WIPERS_CF', field: 'Windshield Driver centre' },
      { id: 'WIPERS_RF', field: 'Windshield Passenger' },
      { id: 'WIPERS_REAR', field: 'Windshield Rear' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = wipersCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const wipersField = form.getRadioGroup(field);
        const options = wipersField.getOptions();

        if (task.redBox) {
          wipersField.select(options[2]);
        } else if (task.yellowBox) {
          wipersField.select(options[1]);
        } else if (task.greenBox) {
          wipersField.select(options[0]);
        }
      }
    });
  }

  populateGMTires(form: PDFForm, inspection: any) {
    const tiresCategory = inspection.tires[0];
    if (!tiresCategory) return;

    const tasks = [{ id: 'TIRES_LF' }, { id: 'TIRES_LR' }, { id: 'TIRES_RF' }, { id: 'TIRES_RR' }];

    tasks.forEach(({ id }) => {
      const task = tiresCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        // driver fron tire
        if (id === 'TIRES_LF') {
          const driverFrontTirePSIField = form.getRadioGroup('Tires Driver Front PSI');
          const driverFrontTirePSIFieldOptions = driverFrontTirePSIField.getOptions();
          // TODO: Logic to determine R/Y/G based on psi in/out?
          //driverFrontTirePSIField.select(driverFrontTirePSIFieldOptions[0]); // placeholder

          this.setFormField(form, 'Driver Front PSI before', task.psiIn?.toString());
          this.setFormField(form, 'Driver Front PSI set to', task.psiOut?.toString());

          const driverFrontTireTreadField = form.getRadioGroup('Tires Driver Front Tread');
          const driverFrontTireTreadFieldOptions = driverFrontTireTreadField.getOptions();

          // TODO: all tire options are 3/2/1
          if (!(task.treadDepth === 0 && task.greyBox)) {
            const index = this.getTreadOptionIndex(task.treadDepth);
            driverFrontTireTreadField.select(driverFrontTireTreadFieldOptions[index]);
          }

          this.setFormField(form, 'Driver front Tread', task.treadDepth?.toString());

          // TODO: how to determine R/Y/G on wear pattern?
          const driverFrontTireWearPatternField = form.getRadioGroup('Tires Driver Front Wear pattern');
          const driverFrontTireWearPatternFieldOptions = driverFrontTireWearPatternField.getOptions();
          //driverFrontTireWearPatternField.select(driverFrontTireWearPatternFieldOptions[2]); // placeholder
        }

        // passenger front tire
        if (id === 'TIRES_RF') {
          const passengerFrontTirePSIField = form.getRadioGroup('Tires Passenger Front PSI');
          const passengerFrontTirePSIFieldOptions = passengerFrontTirePSIField.getOptions();
          // TODO: Logic to determine R/Y/G based on psi in/out?
          //passengerFrontTirePSIField.select(passengerFrontTirePSIFieldOptions[0]); // placeholder

          this.setFormField(form, 'Passenger front PSI before', task.psiIn?.toString());
          this.setFormField(form, 'Passenger front PSI set to', task.psiOut?.toString());

          const passengerFrontTireTreadField = form.getRadioGroup('Tires passenger Front Tread');
          const passengerFrontTireTreadFieldOptions = passengerFrontTireTreadField.getOptions();

          // TODO: all tire options are 3/2/1
          if (!(task.treadDepth === 0 && task.greyBox)) {
            const index = this.getTreadOptionIndex(task.treadDepth);
            passengerFrontTireTreadField.select(passengerFrontTireTreadFieldOptions[index]);
          }

          this.setFormField(form, 'Passenger front Tread', task.treadDepth?.toString());

          // TODO: how to determine R/Y/G on wear pattern?
          const passengerFrontTireWearPatternField = form.getRadioGroup('Tires passenger Front Wear pattern');
          const passengerFrontTireWearPatternFieldOptions = passengerFrontTireWearPatternField.getOptions();
          //passengerFrontTireWearPatternField.select(passengerFrontTireWearPatternFieldOptions[2]); // placeholder
        }

        // driver rear tire
        if (id === 'TIRES_LR') {
          const driverRearTirePSIField = form.getRadioGroup('Tires Driver Rear PSI');
          const driverRearTirePSIFieldOptions = driverRearTirePSIField.getOptions();
          // TODO: Logic to determine R/Y/G based on psi in/out?
          // driverRearTirePSIField.select(driverRearTirePSIFieldOptions[0]); // placeholder

          this.setFormField(form, 'Driver rear PSI before', task.psiIn?.toString());
          this.setFormField(form, 'Driver rear PSI set to', task.psiOut?.toString());

          const driverRearTireTreadField = form.getRadioGroup('Tires Driver Rear Tread');
          const driverRearTireTreadFieldOptions = driverRearTireTreadField.getOptions();

          // TODO: all tire options are 3/2/1
          if (!(task.treadDepth === 0 && task.greyBox)) {
            const index = this.getTreadOptionIndex(task.treadDepth);
            driverRearTireTreadField.select(driverRearTireTreadFieldOptions[index]);
          }

          this.setFormField(form, 'Driver rear Traad', task.treadDepth?.toString());

          // TODO: how to determine R/Y/G on wear pattern?
          const driverRearTireWearPatternField = form.getRadioGroup('Tires Driver Rear Wear pattern');
          const driverRearTireWearPatternFieldOptions = driverRearTireWearPatternField.getOptions();
          //driverRearTireWearPatternField.select(driverRearTireWearPatternFieldOptions[2]); // placeholder
        }

        // passenger rear tire
        if (id === 'TIRES_RR') {
          const passengerRearTirePSIField = form.getRadioGroup('Tires Passenger Rear PSI');
          const passengerRearTirePSIFieldOptions = passengerRearTirePSIField.getOptions();
          // TODO: Logic to determine R/Y/G based on psi in/out?
          // passengerRearTirePSIField.select(passengerRearTirePSIFieldOptions[0]); // placeholder

          this.setFormField(form, 'Passenger rear PSI before', task.psiIn?.toString());
          this.setFormField(form, 'Passenger rear PSI set to', task.psiOut?.toString());

          const passengerRearTireTreadField = form.getRadioGroup('Tires Passenger Rear Tread');
          const passengerRearTireTreadFieldOptions = passengerRearTireTreadField.getOptions();

          // TODO: all tire options are 3/2/1
          if (!(task.treadDepth === 0 && task.greyBox)) {
            const index = this.getTreadOptionIndex(task.treadDepth);
            passengerRearTireTreadField.select(passengerRearTireTreadFieldOptions[index]);
          }

          this.setFormField(form, 'Passenger rear Tread', task.treadDepth?.toString());

          // TODO: how to determine R/Y/G on wear pattern?
          const passengerRearTireWearPatternField = form.getRadioGroup('Tires Passenger rear Wear pattern');
          const passengerRearTireWearPatternFieldOptions = passengerRearTireWearPatternField.getOptions();
          //passengerRearTireWearPatternField.select(passengerRearTireWearPatternFieldOptions[2]); // placeholder

          // TODO: Missing spare tire tasks
        }
      }
    });
  }

  getTreadOptionIndex(depth: number): number {
    if (depth >= 0 && depth <= 3) return 0;
    if (depth > 3 && depth < 6) return 1;
    return 2;
  }

  populateGMTireOther(form: PDFForm, inspection: any) {
    const tireOtherCategory = inspection.tireOther[0];
    if (!tireOtherCategory) return;

    const tasks = [
      { id: 'ROTATION_NEEDED', field: 'Tire Rotation' },
      { id: 'BALANCE_NEEDED', field: 'Balance' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = tireOtherCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const tireOtherField = form.getRadioGroup(field);
        const options = tireOtherField.getOptions();

        // TODO: how to determine R/Y/G on these single boolean toggles for current inspection
        // TODO: missing tires inflated and tire build dates tasks on current inspection
        if (task.redBox) {
          tireOtherField.select(options[2]);
        } else if (task.yellowBox) {
          tireOtherField.select(options[1]);
        } else if (task.greenBox) {
          tireOtherField.select(options[0]);
        }
      }
    });

    //TODO : tire lug nuts have no tasks on current inspection
  }

  populateGMBrakes(form: PDFForm, inspection: any) {
    const brakesCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Check Brakes/Measure Frt and Rear Linings'
    );
    if (!brakesCategory) return;

    // TODO: brake measurements don't match the PDF front and back have different ranges on inspection
    // front brakes match on pdf and inspection
    // rear red on pdf is <=3 on inspection it is <3
    // rear yellow on pdf is 4/5/6, on inspection it is = 3
    // rear greeb on pdf is >= 7, on inspection it is >= 4

    const tasks = [
      { id: 'CHK_BREAKS_LININGS_LF', field: 'Brakes Driver front' },
      { id: 'CHK_BREAKS_LININGS_RF', field: 'Brakes Passenter front' },
      { id: 'CHK_BREAKS_LININGS_LR', field: 'Brakes Driver Rear' },
      { id: 'CHK_BREAKS_LININGS_RR', field: 'Brakes Passenter Rear' },
      { id: 'BREAK_SYS', field: 'Brake system' },
      { id: 'PARKING_BRAKE', field: 'Parking Brake' },
    ];

    tasks.forEach(({ id, field }) => {
      const task = brakesCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const brakeField = form.getRadioGroup(field);
        const options = brakeField.getOptions();

        if (task.redBox) {
          brakeField.select(options[2]);
        } else if (task.yellowBox) {
          brakeField.select(options[1]);
        } else if (task.greenBox) {
          brakeField.select(options[0]);
        }
      }
    });
  }

  populateGMFluids(form: PDFForm, inspection: any, inspectionType: 'ICE' | 'EV') {
    const fluidsCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Check Fluid Levels'
    );
    if (!fluidsCategory) return;

    const tasks = [
      { id: 'ENGINE_OIL', field: 'Engine oil' },
      { id: 'TRANSMISSION_EQUIPED', field: 'Transmission' },
      { id: 'ENGINE_COOLING_SYSTEM', field: 'Engine Cooling' },
      { id: 'BRAKE_FLUID_RESERVOIR', field: 'Brake fluid' },
      { id: 'WINDSHIELD_WASHER', field: 'Windshiel washer' },
      { id: 'DIESEL_EXHAUST_FLUID', field: 'Diesel exhaust' }, //TODO: has choices 1/2/2/3, resulting in greens actually being yellow
      { id: 'POWER_STEERING', field: 'Power steering' },
      { id: 'FUEL_SYSTEM', field: 'Fuel system' },
      { id: 'ENG_TRANS_ETC', field: 'Transfer case' },
    ];

    if (inspectionType === 'ICE') {
      tasks.push({ id: 'DRIVE_AXLE', field: 'Drive axle' }); //TODO: has choices 1/2/1/2/3, resulting in reds actually being green
    }

    if (inspectionType === 'EV') {
      tasks.push({ id: 'DRIVE_UNIT', field: 'Drive unit' });
      tasks.push({ id: 'DRIVE_AXLE', field: 'Drive axle' });
    }

    tasks.forEach(({ id, field }) => {
      const task = fluidsCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const fluidField = form.getRadioGroup(field);
        const options = fluidField.getOptions();

        if (task.redBox) {
          fluidField.select(options[2]);
        } else if (task.yellowBox) {
          fluidField.select(options[1]);
        } else if (task.greenBox) {
          fluidField.select(options[0]);
        }
      }
    });
  }

  populateGMCheckInspect(form: PDFForm, inspection: any, inspectionType: 'ICE' | 'EV') {
    const checkInspectCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Additional Checks'
    );
    if (!checkInspectCategory) return;

    // TODO: lots of missing tasks on current inspection (commented out)
    const tasks = [
      { id: 'HORN', field: 'Horn' },
      { id: 'START_SWITCH_OP', field: 'Starter' },
      { id: 'ACCEL_PEDAL_DMG', field: 'Acceleration' },
      { id: 'IGN_CONTROL_OP', field: 'Ignition lock' },
      //{ id: 'AUTO_TRANS_PARK', field: 'Automatic transmission' },
      //{ id: 'AUTO_TRANS_SHIFT', field: 'Automatic transmission shift' },
      //{ id: 'DRIVER_FLOOR_PEDALS', field: 'Check floor mats' },
      //{ id: 'SUNROOF', field: 'inspect sunroof' },
      //{ id: 'SAFETY_BELT_REMINDER', field: 'Safety belts reminder' },

      { id: 'PASSENGER_AIR_FILTER', field: 'Cabin air filter' },
      { id: 'ENGINE_AIR_FILTER_ETC', field: 'Engine air filter' },
      { id: 'BELTS_ENGINE_ETC', field: 'Belts' },
      { id: 'HOSES_ENGINE_ETC', field: 'Hoses' },

      { id: 'CV_DRIVE_AXLE_BOOTS_ETC', field: 'CV drive' },
      { id: 'EXHAUST_SYS_COMPONENTS', field: 'Exhaust system' },
      //{ id: 'SUSPENSION', field: 'Suspension' }, // Suspension
      { id: 'EVAPORATION_CS', field: 'Evaporative control' },
      { id: 'CHASSIS_COMP_LUBR', field: 'Body components' },
      { id: 'BACKUP_CAMERA', field: 'Backup camera' },
    ];

    if (inspectionType === 'ICE') {
      tasks.push({ id: 'DRIVER_FLOOR_MAT', field: 'Floor mat size' });
      tasks.push({ id: 'DRIVER_FLOOR_MAT', field: 'Floor mat pedals' });
      tasks.push({ id: 'SAFETY_BELT_COMPONENTS', field: 'Belts buckles' });
      tasks.push({ id: 'GAS_STRUTS_HATCH', field: 'Gas struts' });
      tasks.push({ id: 'STEERING_COMPONENTS', field: 'Steering components' });
    }

    if (inspectionType === 'EV') {
      tasks.push({ id: 'SAFETY_BELT_COMPONENTS', field: 'Safety belts' });
      tasks.push({ id: 'GAS_STRUTS_HATCH', field: 'Gas Struts' });
      tasks.push({ id: 'STEERING_COMPONENTS', field: 'Steering' });
      tasks.push({ id: 'DRIVER_FLOOR_MAT', field: 'Mat pedals' });
      tasks.push({ id: 'DRIVER_FLOOR_MAT', field: 'Mat size' });
      tasks.push({ id: 'AXLE_BOOTS', field: 'Axle boots' });
      tasks.push({ id: 'CHARGING_PORT_SEAL', field: 'Port seal' });
      tasks.push({ id: 'CHARGING_PORT_CONNECTOR', field: 'Port seal connector' });
      tasks.push({ id: 'PEDESTRIAN_SAFETY_SIGNAL', field: 'Safety signal' });
    }

    tasks.forEach(({ id, field }) => {
      const task = checkInspectCategory.tasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const checkInspectField = form.getRadioGroup(field);
        const options = checkInspectField.getOptions();

        if (task.redBox) {
          checkInspectField.select(options[2]);
        } else if (task.yellowBox) {
          checkInspectField.select(options[1]);
        } else if (task.greenBox) {
          checkInspectField.select(options[0]);
        }
      }
    });
  }

  handle321ChoiceTasks(form: PDFForm, inspection: any) {
    const tiresOtherCategory = inspection.tireOther[0];

    if (!tiresOtherCategory) return;

    const batteryCategory = inspection.categories?.find((category: any) => category.categoryName === 'Check Battery');
    if (!batteryCategory) return;

    const fluidsCategory = inspection.categories?.find(
      (category: any) => category.categoryName === 'Check Fluid Levels'
    );
    if (!fluidsCategory) return;

    const tasks = [
      { id: 'ALIGNMENT_NEEDED', field: 'Alignment' },
      { id: 'BATTERY_CABLES_AND_CON', field: 'Battery cables' },
      { id: 'EV_COOLING_SYSTEM', field: 'Electric cooling' },
    ];

    const combinedTasks = tiresOtherCategory.tasks.concat(batteryCategory.tasks).concat(fluidsCategory.tasks);

    tasks.forEach(({ id, field }) => {
      const task = combinedTasks.find((task: any) => task.gmInspectionId.includes(id));
      if (task) {
        const taskField = form.getRadioGroup(field);

        const options = taskField.getOptions();

        if (task.redBox) {
          taskField.select(options[0]);
        } else if (task.yellowBox) {
          taskField.select(options[1]);
        } else if (task.greenBox) {
          taskField.select(options[2]);
        }
      }
    });
  }

  fixBroken123GMICERadioGroups(form: PDFForm) {
    const workingRadioGroup = form.getRadioGroup('Horn');
    const workingWidgets = workingRadioGroup.acroField.getWidgets();

    // Array of broken field names
    const brokenFieldNames = [
      'Ignition lock',
      'Floor mat size',
      'Floor mat pedals',
      'Belts buckles',
      'Backup camera',
      'Cabin air filter',
      'Engine air filter',
      'Belts',
      'Gas struts',
      'Steering components',
      'CV drive',
      'Exhaust system',
      'Evaporative control',
      'Body components',
      'Power steering',
    ];

    // TODO: these tasks do not currently exist on our inspection, including them to fix the empty radio styling
    brokenFieldNames.push('Transmission lock');
    brokenFieldNames.push('Sunroof');
    brokenFieldNames.push('Belt reminder');
    brokenFieldNames.push('Suspension');

    brokenFieldNames.forEach((brokenFieldName) => {
      try {
        const brokenRadioGroup = form.getRadioGroup(brokenFieldName);
        const brokenWidgets = brokenRadioGroup.acroField.getWidgets();

        workingWidgets.forEach((workingWidget, index) => {
          if (brokenWidgets[index]) {
            const workingAppDict = workingWidget.dict.get(PDFName.of('AP'));
            if (workingAppDict) {
              // Create a new appearance dictionary instead of reusing the same one
              const clonedAppDict = brokenWidgets[index].dict.context.obj({});

              if (workingAppDict instanceof PDFDict) {
                // Copy Normal, Rollover, and Down appearance states
                ['N', 'R', 'D'].forEach((stateName) => {
                  const state = workingAppDict.get(PDFName.of(stateName));
                  if (state) {
                    clonedAppDict.set(PDFName.of(stateName), state);
                  }
                });
              }

              brokenWidgets[index].dict.set(PDFName.of('AP'), clonedAppDict);
            }
          }
        });
      } catch (error) {
        console.error(`Error fixing ${brokenFieldName}:`, error);
      }
    });
  }

  fixBroken123GMEVRadioGroups(form: PDFForm) {
    const workingRadioGroup = form.getRadioGroup('Battery visual');
    const workingWidgets = workingRadioGroup.acroField.getWidgets();

    // Array of broken field names
    const brokenFieldNames = [
      'Battery test',
      'Drive unit',
      'Drive axle',
      'Transfer case',
      'Electric cooling',
      'Mat pedals',
      'Mat size',
      'Steering',
      'Backup camera',
      'Axle boots',
      'Port seal',
      'Port seal connector',
      'Safety signal',
    ];

    // TODO: these tasks do not currently exist on our inspection, including them to fix the empty radio styling
    brokenFieldNames.push('Suspension components');
    brokenFieldNames.push('Body components');
    brokenFieldNames.push('Underbody');
    brokenFieldNames.push('Belt reminder');

    brokenFieldNames.forEach((brokenFieldName) => {
      try {
        const brokenRadioGroup = form.getRadioGroup(brokenFieldName);
        const brokenWidgets = brokenRadioGroup.acroField.getWidgets();

        workingWidgets.forEach((workingWidget, index) => {
          if (brokenWidgets[index]) {
            const workingAppDict = workingWidget.dict.get(PDFName.of('AP'));
            if (workingAppDict) {
              // Create a new appearance dictionary instead of reusing the same one
              const clonedAppDict = brokenWidgets[index].dict.context.obj({});

              if (workingAppDict instanceof PDFDict) {
                // Copy Normal, Rollover, and Down appearance states
                ['N', 'R', 'D'].forEach((stateName) => {
                  const state = workingAppDict.get(PDFName.of(stateName));
                  if (state) {
                    clonedAppDict.set(PDFName.of(stateName), state);
                  }
                });
              }

              brokenWidgets[index].dict.set(PDFName.of('AP'), clonedAppDict);
            }
          }
        });
      } catch (error) {
        console.error(`Error fixing ${brokenFieldName}:`, error);
      }
    });
  }

  //GM PDF FUNCTIONS END

  // Utility to set a form field with optional fallback and trimming
  private setFormField(
    form: PDFForm,
    fieldName: string,
    value: string | null | undefined,
    options: { fallback?: string; trim?: boolean } = {}
  ): void {
    try {
      const field = form.getTextField(fieldName);

      let textValue = value ?? options.fallback ?? '';
      if (options.trim) {
        textValue = textValue.trim();
      }

      field.setText(textValue);
    } catch (error) {
      console.warn(`PDF form field '${fieldName}' not found or not a text field:`, error);
      // Don't rethrow - silently skip missing fields
    }
  }
}
